import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  Paperclip as PaperclipIcon,
} from '@phosphor-icons/react/dist/ssr';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import type { Announcement } from '@/types/announcement';
import { Announcement_Status, Announcement_Type } from '@/types/announcement';
import { paths } from '@/paths';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { useCommunityRole } from '@/hooks/v2/use-community-role';

interface AnnouncementsTableProps {
  rows: Announcement[];
  isLoading?: boolean;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Published: { bg: '#10B98118', text: '#10B981' },
  Pending:   { bg: '#F59E0B18', text: '#F59E0B' },
  Draft:     { bg: '#6B728018', text: '#6B7280' },
  Processing:{ bg: '#6366F118', text: '#6366F1' },
  Cancelled: { bg: '#EF444418', text: '#EF4444' },
  Failed:    { bg: '#EF444418', text: '#EF4444' },
};

const TYPE_COLORS: Record<string, string> = {
  General: '#3B82F6',
  Maintenance: '#F59E0B',
  Emergency: '#EF4444',
};

function resolveStatus(raw: any): string {
  if (typeof raw === 'number') return (Announcement_Status[raw as keyof typeof Announcement_Status] as string) ?? String(raw);
  return String(raw ?? '');
}

function resolveType(raw: any): string {
  if (typeof raw === 'number') return (Announcement_Type[raw as keyof typeof Announcement_Type] as string) ?? String(raw);
  return String(raw ?? '');
}

export function AnnouncementsTable({ rows, isLoading = false }: AnnouncementsTableProps): React.JSX.Element {
  const navigate = useNavigate();
  const { hasBoardLevelAccess } = useCommunityRole();

  const columns = React.useMemo<MRT_ColumnDef<Announcement>[]>(
    () => [
      {
        accessorKey: 'subject',
        header: 'Subject',
        size: 280,
        grow: true,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        Cell: ({ row }) => (
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} variant="body2">
              {row.original.subject || 'No subject'}
            </Typography>
            {row.original.message && (
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 360,
                }}
                variant="caption"
              >
                {row.original.message.length > 80 ? `${row.original.message.slice(0, 80)}…` : row.original.message}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        size: 130,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: ['General', 'Maintenance', 'Emergency'].map((t) => ({ text: t, value: t })),
        accessorFn: (row) => toTitleCase(resolveType(row.type)),
        Cell: ({ row }) => {
          const label = toTitleCase(resolveType(row.original.type));
          const color = TYPE_COLORS[label] ?? '#6B7280';
          return (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: '2px',
                borderRadius: 1,
                bgcolor: `${color}18`,
                border: `1px solid ${color}40`,
              }}
            >
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color, lineHeight: 1.4 }}>
                {label}
              </Typography>
            </Box>
          );
        },
      },
      // ── Management-only: delivery lifecycle + targeting ──
      ...(hasBoardLevelAccess
        ? ([
            {
              accessorKey: 'status',
              header: 'Status',
              size: 130,
              enableSorting: true,
              enableColumnFilter: true,
              filterVariant: 'select',
              filterSelectOptions: ['Published', 'Pending', 'Draft', 'Processing', 'Cancelled', 'Failed'].map((s) => ({ text: s, value: s })),
              accessorFn: (row) => toTitleCase(resolveStatus(row.status)),
              Cell: ({ row }) => {
                const label = toTitleCase(resolveStatus(row.original.status));
                const style = STATUS_COLORS[label] ?? { bg: '#6B728018', text: '#6B7280' };
                return (
                  <Chip
                    label={label}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      bgcolor: style.bg,
                      color: style.text,
                      border: `1px solid ${style.text}40`,
                    }}
                  />
                );
              },
            },
            {
              accessorKey: 'board_members_only',
              header: 'Visibility',
              size: 120,
              enableSorting: true,
              enableColumnFilter: true,
              filterVariant: 'select',
              filterSelectOptions: [
                { text: 'All Members', value: 'false' },
                { text: 'Board Only', value: 'true' },
              ],
              accessorFn: (row) => (row.board_members_only ? 'Board Only' : 'All Members'),
              Cell: ({ row }) => (
                <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }} variant="body2">
                  {row.original.board_members_only ? 'Board Only' : 'All Members'}
                </Typography>
              ),
            },
          ] as MRT_ColumnDef<Announcement>[])
        : []),
      {
        accessorKey: 'files',
        header: 'Attachments',
        size: 110,
        enableSorting: false,
        enableColumnFilter: false,
        accessorFn: (row) => row.files?.length ?? 0,
        Cell: ({ row }) => {
          const count = row.original.files?.length ?? 0;
          if (count === 0) return <Typography color="text.disabled" sx={{ fontSize: '0.875rem' }} variant="body2">—</Typography>;
          return (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <PaperclipIcon color="var(--mui-palette-text-disabled)" size={14} />
              <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }} variant="body2">
                {count}
              </Typography>
            </Stack>
          );
        },
      },
      // Date column: managers see when it was drafted ("Created"); residents see when
      // it actually went out ("Published") — these differ for scheduled announcements.
      (hasBoardLevelAccess
        ? {
            id: 'date',
            header: 'Created',
            size: 120,
            enableSorting: true,
            enableColumnFilter: false,
            accessorFn: (row) => row.created_at ?? '',
            Cell: ({ row }) => (
              <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }} variant="body2">
                {row.original.created_at ? dayjs(row.original.created_at).format('MMM D, YYYY') : '—'}
              </Typography>
            ),
          }
        : {
            id: 'date',
            header: 'Published',
            size: 120,
            enableSorting: true,
            enableColumnFilter: false,
            accessorFn: (row) => row.processed_on ?? row.created_at ?? '',
            Cell: ({ row }) => {
              const when = row.original.processed_on ?? row.original.created_at;
              return (
                <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }} variant="body2">
                  {when ? dayjs(when).format('MMM D, YYYY') : '—'}
                </Typography>
              );
            },
          }) as MRT_ColumnDef<Announcement>,
      // ── Management-only: author + scheduling ──
      ...(hasBoardLevelAccess
        ? ([
            {
              accessorKey: 'creator',
              header: 'Created by',
              size: 150,
              enableSorting: true,
              enableColumnFilter: false,
              accessorFn: (row) => row.creator?.name ?? '',
              Cell: ({ row }) => {
                const name = row.original.creator?.name;
                return (
                  <Typography
                    color={name ? 'text.secondary' : 'text.disabled'}
                    sx={{ fontSize: '0.875rem' }}
                    variant="body2"
                  >
                    {name || '—'}
                  </Typography>
                );
              },
            },
            {
              accessorKey: 'scheduled_at',
              header: 'Scheduled',
              size: 120,
              enableSorting: true,
              enableColumnFilter: false,
              accessorFn: (row) => (row.scheduled_at ? dayjs(row.scheduled_at).format('MMM D, YYYY') : ''),
              Cell: ({ row }) => (
                <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }} variant="body2">
                  {row.original.scheduled_at ? dayjs(row.original.scheduled_at).format('MMM D, YYYY') : '—'}
                </Typography>
              ),
            },
          ] as MRT_ColumnDef<Announcement>[])
        : []),
    ],
    [hasBoardLevelAccess]
  );

  const table = useMaterialReactTable<Announcement>({
    columns,
    data: rows,
    state: {
      isLoading,
    },
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: false,
      sorting: [{ id: 'date', desc: true }],
      density: 'compact',
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search announcements…',
    },
    enableRowActions: false,
    enableRowSelection: false,
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { minHeight: 400 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', borderRadius: 0 } },
    muiTableProps: { sx: { tableLayout: 'fixed' } },
    muiTableHeadProps: {
      sx: {
        '& tr th': {
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          py: 1.5,
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        '& tr td': {
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: '0.875rem',
          py: 1.5,
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(paths.announcements.announcement(row.original.uuid ?? row.original.id.toString()));
      },
      sx: {
        cursor: 'pointer',
        '&:hover td': { bgcolor: 'action.hover' },
      },
    }),
    renderTopToolbar: ({ table }) => {
      const publishedCount = rows.filter((r) => toTitleCase(resolveStatus(r.status)) === 'Published').length;
      const draftCount = rows.filter((r) => toTitleCase(resolveStatus(r.status)) === 'Draft').length;

      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            px: 2,
            py: 1.5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                ml: 1,
                pl: 1.5,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {publishedCount} published
              </Typography>
              <Typography color="text.disabled" sx={{ fontSize: '0.6875rem' }}>·</Typography>
              <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {draftCount} draft{draftCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Stack>

        </Box>
      );
    },
  });

  return <MaterialReactTable table={table} />;
}
